import ActionButton from 'components/ActionButton';
import BackButton from 'components/BackButton';
import { Loading } from 'components/loading/Loading';
import React, { useEffect, useState } from 'react';
import { fetchConfig, updateWorkingDayConfig } from 'services';
import { CONFIG_KEYS, showError, showSuccess } from 'utils';
import styles from './index.module.scss';
import TimePicker from 'components/Date/TimePicker';
import WeeksDaysPicker from 'components/WeeksDaysPicker';
import WeekZipcodePicker from 'components/WeekAndZipcodePicker';

const LabelField = ({ value }: { value: string | number }) => (
  <div className={styles['label']}>{value}</div>
);

let response: any={};

const ConfigDetails = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [updateLoading, setUpdateLoading] = useState<boolean>(false);
  const [workingHourStart, setWorkingHourStart] = useState<number>(0);
  const [workingHourEnd, setWorkingHourEnd] = useState<number>(0);
  const [routedDeliveryWorkingDays, setRoutedDeliveryWorkingDays] = useState<any>([]);
  const [excludedZipCodesWorkingDays, setExcludedZipCodesWorkingDays] = useState<any>([]);
  const [excludedZipCodesImmediateDelivery, setExcludedZipCodesImmediateDelivery] = useState<any>([]);
  const [immediateDeliveryWorkingDays, setImmediateDeliveryWorkingDays] = useState<any>([]);
  const [automaticRoutesConfig, setAutomaticRoutesConfig] = useState<any>({});

  const fetchConfigFn = async () => {
    try {
      response = await fetchConfig([CONFIG_KEYS.WORKING_DAYS_CONFIG,CONFIG_KEYS.AUTOMATIC_ROUTE_CONFIG]);
      setWorkingHourStart(response?.workingDaysConfig?.workingHourStart ?? 0);
      setWorkingHourEnd(response?.workingDaysConfig?.workingHourEnd ?? 24);
      setRoutedDeliveryWorkingDays(response?.workingDaysConfig?.workingDaysRoutedDelivery ?? []);
      setExcludedZipCodesWorkingDays(response?.workingDaysConfig?.excludeZipcodesForDay ?? []);
      setExcludedZipCodesImmediateDelivery(response?.workingDaysConfig?.excludeZipCodesForImmediateDelivery?.join(',') ?? '');
      setImmediateDeliveryWorkingDays(response?.workingDaysConfig?.workingDaysImmediateDelivery ?? []);
      setAutomaticRoutesConfig({ ...response.automaticRouteConfig, 
        salesChannelsForAutomaticRoute: response?.automaticRouteConfig?.salesChannelsForAutomaticRoute.toString() });
    } catch (e) {
      showError(e);
    } finally {
      setLoading(false);
    }
  };

  const updateWorkingDaysConfigFn = async (fieldsToUpdate: any, successMsg: string, propKey?: string) => {
    const defaultConfigKey = propKey || CONFIG_KEYS.WORKING_DAYS_CONFIG;
    const defaultSpreadResponse = propKey ? response?.automaticRouteConfig : response?.workingDaysConfig;
    try {
      setUpdateLoading(true);
      await updateWorkingDayConfig(defaultConfigKey, { ...defaultSpreadResponse,...fieldsToUpdate });
      setAutomaticRoutesConfig({});
      showSuccess(successMsg);
      await fetchConfigFn();
    } catch (e) {
      showError(e);
    } finally {
      setUpdateLoading(false);
    }
  };

  const handleAutomaticRoutesUpdate = ()=> {
    if((!automaticRoutesConfig.maxOrderPerVehicle || automaticRoutesConfig.maxOrderPerVehicle==0) && 
            (!automaticRoutesConfig.noOfVehiclesAvailable || automaticRoutesConfig.noOfVehiclesAvailable==0)){
      showError('Either Maximum Order Per Vehicle or Number Of Vehicles required.');
      return;
    } else {
      updateWorkingDaysConfigFn({ 
        salesChannelsForAutomaticRoute: (automaticRoutesConfig?.salesChannelsForAutomaticRoute)?.split(',').map(salesChannel=> salesChannel.trim()),
        maxOrderPerVehicle: parseInt(automaticRoutesConfig?.maxOrderPerVehicle),
        maxTravelTimeForRoute: parseInt(automaticRoutesConfig?.maxTravelTimeForRoute),
        noOfVehiclesAvailable: parseInt(automaticRoutesConfig?.noOfVehiclesAvailable),
      }, 
      'Automatic route config updated successfully',
      CONFIG_KEYS.AUTOMATIC_ROUTE_CONFIG,
      ); 
    }
  };
  
  useEffect(() => {
    fetchConfigFn();
  }, []);

  const Header = () => (
    <div className={styles['header']}>
      <div className={styles['heading']}>Config</div>
      <BackButton />
    </div>
  );

  const getWorkingHoursUpdate = () => {
    return (
      <div className={`${styles['basic-details-section']}`} >
        <div className={'d-flex'}>
          <div>
            <LabelField value={'Working Hours Start'} />
            <div className={styles['']}>
              <TimePicker value={workingHourStart} options={{ hour24Mode: true, switchToMinuteOnHourSelect: false }} 
                hourFormat24={true} onChange={setWorkingHourStart} />
            </div>
          </div>
          <div>
            <LabelField value={'Working Hours End'} />
            <div className={styles['']}>
              <TimePicker value={workingHourEnd} options={{ hour24Mode: true, switchToMinuteOnHourSelect: false }} 
                hourFormat24={true} onChange={setWorkingHourEnd} />
            </div>
          </div>
          <ActionButton
            text={'Update Working Hours'}
            active={!updateLoading}
            isLoading={updateLoading}
            onClick={()=>updateWorkingDaysConfigFn({ workingHourStart,workingHourEnd }, 'Working hours updated successfully')}
          />
        </div>
      </div >
    );
  };

  const getWorkingDaysUpdate = () => {
    return (
      <div className={`${styles['basic-details-section']}`}>
        <div className={'d-flex row mx-md-n5'}>
          <div className={'col px-md-5 '}>
            <WeeksDaysPicker workingDays={immediateDeliveryWorkingDays}
              text={'Immediate Delivery Working Days'} onChange={setImmediateDeliveryWorkingDays} />
            <ActionButton
              text={'Update Immediate Delivery Working Days'}
              active={!updateLoading}
              isLoading={updateLoading}
              onClick={()=>updateWorkingDaysConfigFn({ workingDaysImmediateDelivery:immediateDeliveryWorkingDays }, 'Immediate Delivery Working Days Updated.')}
            />
          </div>
          <div className={'col px-md-5'}>
            <WeeksDaysPicker workingDays={routedDeliveryWorkingDays}
              text={'Routed Delivery Working Days'} onChange={setRoutedDeliveryWorkingDays} />
            <ActionButton
              text={'Update Routed Delivery Working Hours'}
              active={!updateLoading}
              isLoading={updateLoading}
              onClick={()=>updateWorkingDaysConfigFn({ workingDaysRoutedDelivery:routedDeliveryWorkingDays }, 'Routed Working Days updated successfully')}
            />
          </div>
        </div>
      </div>
    );
  };

  const getWorkingDaysExcludedZipCodesUpdate = () => {
    return (
      <div className={`${styles['basic-details-section']}`}>
        <div className={'d-flex row mx-md-n5'}>
          <div className={'col px-md-5 '}>
            <WeekZipcodePicker dataFromProps={excludedZipCodesWorkingDays} text={'Excluded Zipcodes for Delivery'} 
              updateLoading={updateLoading} onUpdate={updateWorkingDaysConfigFn}/>
          </div>
        </div>
      </div>
    );
  };

  const getExcludedZipCodesImmediateDeliveryUpdate = () => {
    return (
      <div className={`${styles['basic-details-section']}`}>
        <div className={'d-flex row mx-md-n5'}>
          <div className={'col px-md-5 '}>
            <label>Excluded Zipcodes For Immediate Delivery</label>
            <textarea className="form-control" id="excludedZipCodesImmediateDelivery" value={excludedZipCodesImmediateDelivery} 
              rows={3} onChange={(e)=> setExcludedZipCodesImmediateDelivery(e.target.value)}></textarea>
            <span className='text-muted small mb-3'>Seperate multiple zipcodes with comma</span>
            <ActionButton
              text={'Update Excluded Zipcodes for Immediate Delivery'}
              active={!updateLoading}
              isLoading={updateLoading}
              onClick={()=> updateWorkingDaysConfigFn({ excludeZipCodesForImmediateDelivery: (excludedZipCodesImmediateDelivery).split(',').map(zipcode=> zipcode.trim()) }, 
                'Excluded Zipcodes for Immediate Delivery updated successfully')}
            />
          </div>
        </div>
      </div>
    );
  };

  const getAutomaticRoutesConfig = () => {
    return (
      <div className={`${styles['basic-details-section']}`}>
        <div className={'d-flex row mx-md-n5'}>
          <div className={'col-6 px-md-5 '}>
            <LabelField value={'Number Of Vehicles Available'}/>
            <input 
              value={automaticRoutesConfig?.noOfVehiclesAvailable}
              type={'number'}
              min={0}
              className='form-control'
              disabled={updateLoading} 
              onChange={(e)=> setAutomaticRoutesConfig({ ...automaticRoutesConfig, noOfVehiclesAvailable: e.target.value })}/>
            {automaticRoutesConfig?.noOfVehiclesAvailable==0 && <span className='text-muted small mb-3'>It will calculate it based on no of available orders</span>}
          </div>
          <div className={'col-6 px-md-5 '}>
            <LabelField value={'Sales Channel For Automatic Route'}/>
            <textarea 
              className="form-control" 
              rows={3}
              value={automaticRoutesConfig?.salesChannelsForAutomaticRoute}
              disabled={updateLoading} 
              onChange={(e)=> setAutomaticRoutesConfig({ ...automaticRoutesConfig, salesChannelsForAutomaticRoute: e.target.value })}/>
            <span className='text-muted small mb-3'>Seperate multiple zipcodes with comma</span>
          </div>
          <div className={'col-6 px-md-5 '}>
            <LabelField value={'Max Time Travel For Route (in mins)'}/>
            <input 
              value={automaticRoutesConfig?.maxTravelTimeForRoute}
              type={'number'}
              className='form-control'
              disabled={updateLoading} 
              onChange={(e)=> setAutomaticRoutesConfig({ ...automaticRoutesConfig, maxTravelTimeForRoute: e.target.value })}/>
          </div>
          <div className={'col-6 px-md-5 '}>
            <LabelField value={'Max Order Per Vehicle'}/>
            <input 
              value={automaticRoutesConfig?.maxOrderPerVehicle}
              type={'number'}
              min={0}
              className='form-control'
              disabled={updateLoading} 
              onChange={(e)=> setAutomaticRoutesConfig({ ...automaticRoutesConfig, maxOrderPerVehicle: e.target.value })}/>
            {automaticRoutesConfig?.maxOrderPerVehicle==0 && <span className='text-muted small mb-3'>It will calculate the value based on no of available vehicles</span>}
          </div>
        </div>
        <ActionButton
          text={'Update Automatic Routes Config'}
          active={!updateLoading}
          isLoading={updateLoading}
          onClick={handleAutomaticRoutesUpdate}
        />
      </div>
    );
  };

  if (loading) return <Loading />;

  return (
    <div>
      <Header />
      <div className='card mt-2'>
        <div className='card-header'>
          <div className={styles['subheading']}>Working Days Config</div>
        </div>
        {getWorkingHoursUpdate()}
        {getWorkingDaysUpdate()}
        {getWorkingDaysExcludedZipCodesUpdate()}
        {getExcludedZipCodesImmediateDeliveryUpdate()}
      </div>
      <div className='card mt-3 mb-2'>
        <div className='card-header'>
          <div className={styles['subheading']}>Automatic Route Config</div>
        </div>
        {getAutomaticRoutesConfig()}
      </div>
    </div >
  );
};

export default ConfigDetails;